/* eslint-disable no-case-declarations */
import { useMemo } from 'react';
import { PieChart, Tooltip, Pie, Cell } from 'recharts';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';

// Components
import TooltipCustom from './components/TooltipCustom';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

const SingleOuterPieChart = ({
    data,
    userNumbersFormat,
    height = 135,
    isExport,
    className,
    activeTable
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const finalData = useMemo(() => {
        if (data.graphData) {
            switch (data.id) {
                case 'ages_genders_distribution':
                case 'reached_audience_demographics_distribution':
                    let womenData = 0;
                    let menData = 0;
                    data.graphData.map((item) => {
                        womenData += item?.female ?? 0;
                        menData += item?.male ?? 0;
                    });
                    const totalData = womenData + menData;
                    return {
                        ...data,
                        graphData: [
                            {
                                gender: 'female',
                                value: womenData,
                                percentage: womenData / totalData
                            },
                            { gender: 'male', value: menData, percentage: menData / totalData }
                        ]
                    };
                default:
                    return data;
            }
        }

        return data;
    }, [data]);

    return (
        <>
            {!activeTable && (
                <div
                    className={clsx(
                        'flex min-w-[10rem] items-center justify-center lg:flex-col',
                        className
                    )}
                    style={{ minHeight: height }}>
                    <PieChart height={height} width={height} margin={{ right: -10 }}>
                        <Tooltip
                            cursor={false}
                            content={
                                <TooltipCustom
                                    data={{ ...data, id: `${data.id}-pie` }}
                                    tooltipTranslation={finalData.pieTooltipTranslation}
                                    valueFormat={finalData.pieValueFormat}
                                />
                            }
                        />
                        <Pie
                            isAnimationActive={!isExport}
                            data={finalData.graphData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            startAngle={90}
                            endAngle={450}
                            outerRadius="100%"
                            innerRadius="80%"
                            stroke={0}>
                            {finalData.graphData.map((item, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        darkModeColorsChart[theme][
                                            data.color[Object.values(item)[0]]
                                        ]
                                    }
                                />
                            ))}
                        </Pie>
                    </PieChart>
                    <div className="mb-7 ml-8 mt-8 w-full max-w-[5.6rem] lg:ml-0">
                        {finalData.graphData.map((item, index) => (
                            <p key={index} className="mb-2 flex items-center justify-between">
                                <span className="text-xs">{t(item.gender)}</span>
                                <span
                                    className="text-xxs"
                                    style={{
                                        color: darkModeColorsChart[theme][data.color[item.gender]]
                                    }}>
                                    {formatNumber(item.percentage, finalData.pieValueFormat)}
                                </span>
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

SingleOuterPieChart.propTypes = {
    data: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    height: PropTypes.number,
    className: PropTypes.string
};

export default SingleOuterPieChart;
